<template>
  <div v-if="visible">
    <button
      class="tw-fixed tw-bottom-6 tw-right-6 tw-w-14 tw-h-14 tw-bg-fb-accent tw-text-white tw-rounded-full tw-shadow-lg hover:tw-shadow-xl hover:tw-scale-110 tw-transition tw-flex tw-items-center tw-justify-center tw-text-2xl tw-z-fb-btn"
      :class="{ 'tw-animate-fb-ring': unseen > 0 }"
      title="Assistenza"
      aria-label="Apri assistenza"
      @click="onButtonClick"
    >
      💬
      <span
        v-if="unseen > 0"
        class="tw-absolute -tw-top-1 -tw-right-1 tw-bg-red-500 tw-text-white tw-text-xs tw-font-bold tw-rounded-full tw-min-w-[20px] tw-h-5 tw-px-1 tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-white"
        >{{ unseen }}</span
      >
    </button>

    <FeedbackModal
      v-model="showModal"
      :my-feedback="myFeedback"
      :page-url="currentPageUrl"
      @refresh="load"
      @submitted="load"
    />
  </div>
</template>

<script>
import feedbackService from "../services/feedbackService";
import FeedbackModal from "./FeedbackModal.vue";

export default {
  name: "FeedbackWidget",
  components: { FeedbackModal },
  inject: ["feedbackConfig"],
  props: {
    activePollInterval: { type: Number, default: 10000 },
    idlePollInterval: { type: Number, default: 45000 },
    hiddenPollInterval: { type: Number, default: 120000 },
  },
  data() {
    return {
      showModal: false,
      myFeedback: [],
      unseen: 0,
      timer: null,
      pollingInFlight: false,
      notified: new Set(),
    };
  },
  computed: {
    visible() {
      const fn = this.feedbackConfig?.isAuthenticated;
      return typeof fn === "function" ? fn() !== false : true;
    },
    currentPageUrl() {
      // `path` e non `fullPath`: la query può contenere ?redirect=/... o token e
      // non deve uscire dal browser insieme al ticket [sc-47909].
      return this.$route ? this.$route.path : window.location.pathname;
    },
  },
  watch: {
    showModal() {
      this.scheduleNext();
    },
    // Se l'autenticazione cambia a runtime senza remount: avvia/ferma senza chiamare API da spenti.
    visible(val) {
      if (val) this.start();
      else {
        this.stopPolling();
        this.unseen = 0;
        this.notified = new Set();
      }
    },
  },
  mounted() {
    // I listener DOM si possono attaccare sempre: i loro handler passano da load(), che è
    // protetto da `visible`. Nessuna chiamata API parte se l'utente non è autenticato.
    document.addEventListener("visibilitychange", this.onVisible);
    window.addEventListener("focus", this.refreshNow);
    window.addEventListener("feedback:refresh", this.onExternalFeedbackRefresh);
    if (this.visible) this.start();
  },
  beforeUnmount() {
    this.stopPolling();
    document.removeEventListener("visibilitychange", this.onVisible);
    window.removeEventListener("focus", this.refreshNow);
    window.removeEventListener(
      "feedback:refresh",
      this.onExternalFeedbackRefresh
    );
  },
  methods: {
    notify(msg, opts) {
      this.feedbackConfig?.notify?.(msg, opts);
    },
    // Nel CRM tutti sono richiedenti assistenza; il support/admin vive su assistenza-central.
    // La bolla è SEMPRE user-facing: nessun accesso pannello da valutare, nessun
    // /feedback/access/me, nessun ramo admin [sc-47544 8C-Emma-5/6].
    start() {
      this.refreshNow();
    },
    stopPolling() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    },
    computeInterval() {
      if (typeof document !== "undefined" && document.hidden)
        return this.hiddenPollInterval;
      return this.showModal ? this.activePollInterval : this.idlePollInterval;
    },
    scheduleNext() {
      this.stopPolling();
      if (!this.visible) return; // niente polling da non autenticati
      this.timer = setTimeout(this.tick, this.computeInterval());
    },
    async tick() {
      await this.load();
      this.scheduleNext();
    },
    refreshNow() {
      this.load();
      this.scheduleNext();
    },
    onVisible() {
      if (document.visibilityState === "visible") this.refreshNow();
    },
    onExternalFeedbackRefresh(event) {
      this.load(event?.detail?.silent === true);
    },
    async load(silent = false) {
      if (!this.visible) {
        this.stopPolling();
        return;
      } // guardia API
      if (this.pollingInFlight) return;
      this.pollingInFlight = true;
      try {
        await this.loadUser(silent);
      } catch (err) {
        if (err?.response?.status === 401) this.stopPolling();
      } finally {
        this.pollingInFlight = false;
      }
    },
    notifKey(t) {
      const stamp = t.lastMessageAt || t.latestMessage?.createdAt || "";
      return `${t.id}:${stamp}`;
    },
    async loadUser(silent = false) {
      const { data } = await feedbackService.mine();
      if (!data || !data.ok) return;
      this.myFeedback = data.feedback || [];
      const currentUnread = this.myFeedback.filter((t) => t.unread);
      this.unseen =
        typeof data.unseen === "number" ? data.unseen : currentUnread.length;
      const keys = currentUnread.map((t) => this.notifKey(t));
      const newOnes = keys.filter((k) => !this.notified.has(k));
      if (!silent && !this.showModal && newOnes.length > 0) {
        const n = newOnes.length;
        this.notify(
          n === 1
            ? "Hai una nuova risposta dal team! — Clicca 💬 per leggere"
            : `Hai ${n} ticket con nuove risposte! — Clicca 💬 per leggere`,
          { type: "info", duration: 7000, onClick: () => this.openModal() }
        );
      }
      this.notified = new Set(keys);
    },
    onButtonClick() {
      // Nel CRM tutti sono richiedenti assistenza; il support/admin vive su assistenza-central.
      // Il click apre SEMPRE la modale utente, mai il vecchio pannello interno [sc-47544 8C-Emma-5].
      this.openModal();
    },
    async openModal() {
      this.showModal = true;
      // Analytics: nessuna PII, solo il fatto che l'assistenza è stata aperta.
      // Il correlation id lo aggiunge l'host tramite il tracker iniettato.
      try {
        this.feedbackConfig?.track?.("support_widget_opened", {});
      } catch {
        /* analytics best-effort */
      }
      await this.load();
    },
  },
};
</script>
